import AppCommandBar from 'app/AppCommandBar'
import { FlexSpinner } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { FileUpload } from 'components/FileUpload/FileUpload'
import { StickyPane } from 'components/StickyPane'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { FormikActions } from 'formik'
import {
  OrderIdForm,
  OrderIdFormSchema,
  OrderIdFormValues,
} from 'forms/OrderIdForm'
import {
  Blob,
  OrderId,
  OrderIdInsertType,
  OrderIdSetType,
  UserRole,
} from 'gql.generated'
import { Box } from 'grid'
import { omitDeep } from 'helpers'
import {
  useCommand,
  useCreateCommand,
  useCustomSearchCommand,
  useDeselectCommand,
  useEditCommand,
} from 'hooks/useCommand'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { useRouteParams } from 'hooks/useRouteParams'
import { useSelection } from 'hooks/useSelection'
import {
  Dialog,
  DialogType,
  IconButton,
  PrimaryButton,
} from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { FormType } from 'views/helpers'
import {
  ReadOrderIdsQueryVariables,
  useCreateOrderIdMutation,
  useCreateOrUpdateFromCsvMutation,
  useDeleteOrderIdMutation,
  useReadOrderIdsQuery,
  useUpdateOrderIdMutation,
} from './AdminOrderIdsView.generated'
import { OrderIdList } from './OrderIdList'

const AdminOrderIdsListView: React.FunctionComponent = () => {
  const { t } = useTranslation()

  const [createOrderId] = useCreateOrderIdMutation()
  const [importFromExcel] = useCreateOrUpdateFromCsvMutation()
  const [updateOrderId] = useUpdateOrderIdMutation()
  const [deleteOrderId] = useDeleteOrderIdMutation()
  const [formType, setFormType] = React.useState()
  const [selectedItemId, setSelectedItemId] = React.useState()
  const [uploadVisible, setUploadVisible] = React.useState(false)
  const [isUploading, setIsUploading] = React.useState(false)
  const [showUploadSuccessDialog, setShowUploadSuccessDialog] =
    React.useState(false)
  const [uploadDialogMessage, setUploadDialogMessage] = React.useState('')
  const [routeParams, updateRouteParams] = useRouteParams(
    'admin.data.orderIds',
    {
      search: undefined,
    }
  )

  useCustomSearchCommand(
    {
      value: routeParams.search,
      placeholder: t('model.orderId.search'),
      onSearch: (value) => updateRouteParams({ search: value }),
      onClear: () => updateRouteParams({ search: undefined }),
    },
    [t, routeParams, updateRouteParams]
  )

  const { loading, data, error, refetch } = useReadOrderIdsQuery({
    variables: { search: routeParams.search },
  })

  const items =
    data && data.orderIds ? data.orderIds.filter((x) => !x._deleted) : []

  const [selection, selectedItems] = useSelection<OrderId>({
    getKey: (item) => item._id!,
  })

  const [panelState, showPanel, hidePanel] = useFormikPanelState()
  const [dialogState, , hideDialog] = useDialogState()

  const handleSubmit = async (
    values: OrderIdFormValues,
    formikActions: FormikActions<OrderIdFormValues>
  ) => {
    formikActions.setSubmitting(true)

    if (formType === FormType.insert) {
      await createOrderId({ variables: { input: values as OrderIdInsertType } })
    } else if (formType === FormType.update) {
      await updateOrderId({
        variables: { id: selectedItemId, update: values as OrderIdSetType },
      })
    } else {
      throw new Error(
        ' Custom: Error while OderId creat or update. FormType not set.'
      )
    }
    formikActions.setSubmitting(false)
    selection.setAllSelected(false)
    refetch({
      variables: { search: routeParams.search },
    } as ReadOrderIdsQueryVariables)
    hidePanel()
  }

  const handleCsvUpload = React.useCallback(
    async (files: Partial<Blob>[]) => {
      setIsUploading(true)
      if (files[0]) {
        const csv = files[0]
        if (csv.uri) {
          const importResult = await importFromExcel({
            variables: {
              uri: csv.uri,
            },
          })
          const importCounts =
            importResult.data && importResult.data.createOrUpdateFromCsv
              ? importResult.data.createOrUpdateFromCsv
              : undefined
          if (importCounts) {
            setShowUploadSuccessDialog(true)
            setUploadDialogMessage(
              `Datensätze verarbeitet: ${importCounts.total}, davon neu erstellt: ${importCounts.created}`
            )
          }
        }
      }
      await refetch()
      setIsUploading(false)
    },
    [
      setIsUploading,
      importFromExcel,
      refetch,
      setShowUploadSuccessDialog,
      setUploadDialogMessage,
    ]
  )

  const handleConfirmDelete = async () => {
    if (selectedItems.length > 0) {
      const ids = selectedItems.map((x) => x._id)
      await deleteOrderId({
        variables: { ids },
      })
    }
    refetch({
      variables: { search: routeParams.search },
    } as ReadOrderIdsQueryVariables)
    hideDialog()
  }

  /** commands */
  useCreateCommand(
    'orderId',
    {
      priority: 10,
      onClick: () => {
        setFormType(FormType.insert)
        showPanel({} as OrderIdInsertType)
      },
    },
    [setFormType, showPanel]
  )
  useCommand(
    {
      disabled: isUploading,
      key: 'importOrderId',
      text: t('CSV Import'),
      iconProps: { iconName: 'BulkUpload' },
      priority: 15,
      onClick: () => {
        setUploadVisible(true)
      },
    },
    [isUploading, setUploadVisible]
  )
  useEditCommand(
    { name: 'orderId', count: selectedItems.length },
    {
      priority: 20,
      onClick: () => {
        setFormType(FormType.update)
        setSelectedItemId(selectedItems[0]._id)
        showPanel(
          omitDeep(selectedItems[0], [
            'modifiedBy',
            'modified',
            'id',
            '__typename',
          ]) as OrderIdSetType
        )
      },
    },
    [selectedItems, setFormType, setSelectedItemId, showPanel]
  )
  // useDeleteCommand(
  //   { name: 'orderId', count: selectedItems.length },
  //   {
  //     priority: 30,
  //     onClick: showDialog,
  //   },
  //   [selectedItems]
  // )

  /** farCommands */
  useDeselectCommand(
    selectedItems.length,
    {
      priority: 10,
      farCommand: true,
      onClick: () => selection.setItems([], true),
    },
    [selectedItems]
  )

  return (
    <FlexSpinner
      loading={loading || isUploading}
      error={error}
      label={
        isUploading
          ? 'Daten werden importiert, dies kann einen Moment dauern...'
          : undefined
      }
    >
      {uploadVisible && (
        <>
          <IconButton
            style={{ float: 'right' }}
            iconProps={{ iconName: 'Cancel' }}
            onClick={() => setUploadVisible(false)}
            text={t('actions.cancel')}
          />
          <FileUpload
            accept={'.csv'}
            singleFile={true}
            parentId={'orderIds'}
            parentType={'OrderId'}
            onUploadCompleted={handleCsvUpload}
            text={t(
              `Klicken oder .csv Datei per Drag'n'Drop hier her ziehen zum Import`
            )}
          />
        </>
      )}
      <OrderIdList
        items={items}
        selection={selection}
        onItemInvoked={() =>
          selectedItems.length === 1
            ? (() => {
                setFormType(FormType.update)
                setSelectedItemId(selectedItems[0]._id)
                showPanel(
                  omitDeep(selectedItems[0], [
                    'modifiedBy',
                    'modified',
                    'id',
                    '__typename',
                  ]) as OrderIdSetType
                )
              })()
            : false
        }
      />
      <FormikPanel
        {...panelState}
        validationSchema={new OrderIdFormSchema()}
        onSubmit={handleSubmit}
        headerText={
          panelState.initialValues && panelState.initialValues._id
            ? t('model.orderId.edit')
            : t('model.orderId.create')
        }
      >
        <OrderIdForm />
      </FormikPanel>
      <ConfirmDeleteDialog
        {...dialogState}
        onConfirm={handleConfirmDelete}
        itemCount={selectedItems.length}
      />
      <Dialog
        hidden={!showUploadSuccessDialog}
        type={DialogType.normal}
        dialogContentProps={{
          title: t('CSV Import erfolgreich!'),
          subText: uploadDialogMessage,
        }}
        modalProps={{
          isBlocking: true,
          styles: { main: { maxWidth: 450 } },
        }}
      >
        <Box mt={2}>
          <PrimaryButton
            onClick={() => {
              setShowUploadSuccessDialog(false)
              setUploadVisible(false)
            }}
          >
            {t('action.ok')}
          </PrimaryButton>
        </Box>
      </Dialog>
    </FlexSpinner>
  )
}

export const AdminOrderIdsView: React.FC = () => {
  return (
    <ViewAuthorizer
      roles={[UserRole.ResourceManager, UserRole.SystemAdministrator]}
    >
      <StickyPane>
        <AppCommandBar />
        <AdminOrderIdsListView />
      </StickyPane>
    </ViewAuthorizer>
  )
}
